import { Canvas, useThree } from '@react-three/fiber';
import { Suspense, useState } from 'react';
import { getCardById, type CardDef } from '@plotjack/core';
import { FoilCard } from './FoilCard';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { getCardTexture } from '../../art/cardTexture';

interface Props {
  hand: string[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPlay?: (id: string) => void;
  energy: number;
  canPlaySelected?: boolean;
}

export function CardHand3D({ hand, selectedId, onSelect, onPlay, energy, canPlaySelected }: Props) {
  const reduced = useReducedMotion();
  const [playAnim, setPlayAnim] = useState<string | null>(null);

  const cards = hand.map((id) => getCardById(id)).filter(Boolean) as CardDef[];

  const handlePlay = (id: string) => {
    const card = getCardById(id);
    if (!card || card.cost > energy) return;
    setPlayAnim(id);
    setTimeout(() => {
      setPlayAnim(null);
      onPlay?.(id);
    }, reduced ? 200 : 550);
  };

  return (
    <div className="card-hand-3d" aria-label="Your hand">
      <Canvas
        camera={{ position: [0, 0, 3], fov: 35 }}
        dpr={Math.min(window.devicePixelRatio, 2)}
        className="card-canvas"
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[2, 4, 5]} intensity={1.2} />
        <Suspense fallback={null}>
          <HandLayout
            cards={cards}
            selectedId={selectedId}
            playAnim={playAnim}
            energy={energy}
            reduced={reduced}
            onSelect={onSelect}
            onPlay={handlePlay}
            canPlaySelected={canPlaySelected}
          />
        </Suspense>
      </Canvas>
      <ul className="card-hand-keys" aria-label="Cards in hand">
        {cards.map((card) => (
          <li key={card.id}>
            <button
              type="button"
              className="card-key"
              aria-pressed={selectedId === card.id}
              disabled={card.cost > energy}
              onClick={() => onSelect(card.id)}
              onDoubleClick={() => handlePlay(card.id)}
            >
              {card.name} <span aria-hidden="true">({card.cost} Ink)</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="card-hand-hint card-hand-hint--desktop">
        Desktop: drag a selected card upward to play, or use Play card below.
      </p>
    </div>
  );
}

interface LayoutProps {
  cards: CardDef[];
  selectedId: string | null;
  playAnim: string | null;
  energy: number;
  reduced: boolean;
  onSelect: (id: string) => void;
  onPlay: (id: string) => void;
  canPlaySelected?: boolean;
}

/** Fans the hand to fit the canvas: cards stay as large as the height allows, spacing shrinks with hand size. */
function HandLayout({
  cards,
  selectedId,
  playAnim,
  energy,
  reduced,
  onSelect,
  onPlay,
  canPlaySelected,
}: LayoutProps) {
  const { viewport } = useThree();
  const n = Math.max(cards.length, 1);
  const scale = Math.min((viewport.height * 0.86) / 1.2, 1.6);
  const cardW = 0.85 * scale;
  const spacing = Math.min(cardW * 1.08, (viewport.width * 0.92 - cardW) / Math.max(n - 1, 1));
  return (
    <group>
      {cards.map((card, i) => {
        const t = i - (n - 1) / 2;
        const angle = -t * 0.06;
        const selected = selectedId === card.id;
        const playing = playAnim === card.id;
        const y = playing ? viewport.height : selected ? 0.08 * scale : -Math.abs(t) * 0.03 * scale - 0.02;
        return (
          <group key={card.id} scale={scale}>
            <FoilCard
              position={[(t * spacing) / scale, y / scale, playing ? 0.5 : selected ? 0.12 : i * 0.002]}
              rotation={[0, 0, playing ? 0 : angle]}
              selected={selected}
              texture={getCardTexture(card, 30 + i * 12)}
              foil={card.id.endsWith('_foil') || card.id.startsWith('scar_')}
              reducedMotion={reduced}
              onClick={() => onSelect(card.id)}
              onDoubleClick={() => onPlay(card.id)}
              onDragPlay={() => {
                if (selectedId === card.id && canPlaySelected) onPlay(card.id);
              }}
              dimmed={card.cost > energy}
            />
          </group>
        );
      })}
    </group>
  );
}
